/**
 * RTVI Custom Message Types
 *
 * Type definitions for custom RTVI messages sent from the backend
 * media pipeline during episode conversations.
 */

/**
 * Goal completion event data sent when LLM detects user has achieved a learning goal
 */
export interface GoalCompletedData {
  message_type: 'goal_completed';
  goal_id: string;
  timestamp: string; // ISO 8601 datetime
}

/**
 * Custom RTVI message for goal completion
 */
export interface GoalCompletedMessage {
  type: 'rtvi-custom-message';
  data: GoalCompletedData;
}

/**
 * Type guard to check if an RTVI message is a goal completion message
 */
export function isGoalCompletedMessage(message: unknown): message is GoalCompletedMessage {
  if (typeof message !== 'object' || message === null) {
    return false;
  }

  const msg = message as Record<string, unknown>;
  if (msg.type !== 'rtvi-custom-message') {
    return false;
  }

  const data = msg.data as Record<string, unknown> | undefined;
  return (
    data !== undefined &&
    data.message_type === 'goal_completed' &&
    typeof data.goal_id === 'string' &&
    typeof data.timestamp === 'string'
  );
}

/**
 * Parsed goal completion event for UI display
 */
export interface GoalCompletionEvent {
  goalId: string;
  timestamp: Date;
  displayTime: string; // Formatted time for UI (e.g., "2:35 PM")
}

/**
 * Session end request reasons
 * Story 3.9.6: Intentional Disconnect Handling
 */
export type SessionEndReason = 'user_action' | 'navigation';

/**
 * Session end request data
 * Sent from client to backend to signal intentional disconnect
 */
export interface SessionEndRequestData {
  type: 'request.session.end';
  reason: SessionEndReason;
}

/**
 * Type guard to check if a message is a session end request
 */
export function isSessionEndRequest(message: unknown): message is SessionEndRequestData {
  if (typeof message !== 'object' || message === null) {
    return false;
  }

  const msg = message as Record<string, unknown>;
  return (
    msg.type === 'request.session.end' &&
    (msg.reason === 'user_action' || msg.reason === 'navigation')
  );
}

/**
 * Session pause request data
 * Story 3.9.8: Pause and Resume Functionality
 *
 * Sent from client to backend when user pauses the conversation.
 * Microphone is muted client-side before sending this message.
 * Backend will cancel ongoing TTS but keep idle detection running.
 */
export interface SessionPauseRequestData {
  type: 'pause.client.session';
}

/**
 * Session resume request data
 * Story 3.9.8: Pause and Resume Functionality
 *
 * Sent from client to backend when user resumes the conversation.
 * Microphone is unmuted client-side before sending this message.
 * Backend will log the resume event and continue normal operation.
 */
export interface SessionResumeRequestData {
  type: 'resume.client.session';
}

/**
 * Type guard to check if a message is a session pause request
 */
export function isSessionPauseRequest(message: unknown): message is SessionPauseRequestData {
  if (typeof message !== 'object' || message === null) {
    return false;
  }

  const msg = message as Record<string, unknown>;
  return msg.type === 'pause.client.session';
}

/**
 * Type guard to check if a message is a session resume request
 */
export function isSessionResumeRequest(message: unknown): message is SessionResumeRequestData {
  if (typeof message !== 'object' || message === null) {
    return false;
  }

  const msg = message as Record<string, unknown>;
  return msg.type === 'resume.client.session';
}

/**
 * Hint request data
 * Story 3.9.22: Stuck Mode - Text Hints
 *
 * Sent from client to backend when user requests a hint during an active episode.
 */
export interface HintRequestData {
  type: 'hint.request.client';
  timestamp: string; // ISO 8601 datetime
}

/**
 * Hint response data
 * Story 3.9.22: Stuck Mode - Text Hints
 *
 * Sent from backend to client with the generated hint text.
 */
export interface HintResponseData {
  type: 'hint.response.server';
  hint_text: string;
  error?: string;
  timestamp: string; // ISO 8601 datetime
}

/**
 * Type guard to check if a message is a hint request
 */
export function isHintRequest(message: unknown): message is HintRequestData {
  if (typeof message !== 'object' || message === null) {
    return false;
  }

  const msg = message as Record<string, unknown>;
  return msg.type === 'hint.request.client';
}

/**
 * Type guard to check if a message is a hint response
 */
export function isHintResponse(message: unknown): message is HintResponseData {
  if (typeof message !== 'object' || message === null) {
    return false;
  }

  const msg = message as Record<string, unknown>;
  return msg.type === 'hint.response.server';
}
